import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { storage } from '@/utils/storage'

type Theme = 'light' | 'dark'

interface ThemeContextType {
    theme: Theme
    toggleTheme: () => void
    setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

/** Apply .dark on <html>. When `instant`, skip CSS transitions (see `.theme-switching` in index.css). */
function applyThemeToDocument(newTheme: Theme, instant: boolean) {
    const root = document.documentElement
    if (instant) {
        root.classList.add('theme-switching')
    }
    if (newTheme === 'dark') {
        root.classList.add('dark')
    } else {
        root.classList.remove('dark')
    }
    if (!instant) return
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            root.classList.remove('theme-switching')
        })
    })
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [theme, setThemeState] = useState<Theme>('light')

    useEffect(() => {
        const savedTheme = storage.local.getItem('theme') as Theme | null

        if (savedTheme === 'dark' || savedTheme === 'light') {
            setThemeState(savedTheme)
            applyThemeToDocument(savedTheme, false)
        } else {
            const initialTheme: Theme = 'light'
            setThemeState(initialTheme)
            applyThemeToDocument(initialTheme, false)
            storage.local.setItem('theme', initialTheme)
        }
    }, [])

    const setTheme = useCallback((newTheme: Theme) => {
        setThemeState(newTheme)
        applyThemeToDocument(newTheme, true)
        storage.local.setItem('theme', newTheme)
    }, [])

    const toggleTheme = useCallback(() => {
        const newTheme: Theme = theme === 'light' ? 'dark' : 'light'
        setTheme(newTheme)
    }, [theme, setTheme])

    const value = useMemo(
        () => ({ theme, toggleTheme, setTheme }),
        [theme, toggleTheme, setTheme],
    )

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    )
}

export function useTheme() {
    const context = useContext(ThemeContext)
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider')
    }
    return context
}
